import * as R from 'ramda';

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Autocomplete from '@material-ui/lab/Autocomplete';
import Grid from '@material-ui/core/Grid';
import { IMap } from '../../../store/aiearth/types';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import { updateMap } from '../../../store/aiearth/action';

// tslint:disable-next-line: prefer-const
let autocompleteService: any = { current: null };

const initAutocompleteService = async (maps: any): Promise<boolean> => {
  if (!maps) {
    return false;
  }
  if (autocompleteService.current) {
    return true;
  }
  if (maps.places?.AutocompleteService) {
    autocompleteService.current = new maps.places.AutocompleteService();
    return true;
  }
  if (typeof maps.importLibrary === 'function') {
    await maps.importLibrary('places');
    if (maps.places?.AutocompleteService) {
      autocompleteService.current = new maps.places.AutocompleteService();
      return true;
    }
  }
  return false;
};

const PlacesSearch: React.FC<any> = (props) => {
  const { gMaps } = props;
  // Default to autofocus for backwards compatibility; callers that remount this
  // component (e.g. to clear it) can opt out so focus is not stolen.
  const autoFocus = props.autoFocus !== false;
  const [value, setValue] = useState<any>(props.value ? props.value : '');
  const [inputValue, setInputValue] = useState<string>('');
  const [options, setOptions] = useState<any>([]);
  const [placesReady, setPlacesReady] = useState(false);
  const mapStore: any = useSelector<any>((state): any => state);
  const dispatch = useDispatch();
  const fetch = useMemo(
    () =>
      throttle((request: any, callback: any) => {
        autocompleteService.current?.getPlacePredictions?.(request, callback);
      }, 200),
    []
  );

  useEffect(() => {
    let active = true;

    const loadPlaces = async () => {
      if (!gMaps) {
        if (active) {
          setPlacesReady(false);
        }
        return;
      }
      const ready = await initAutocompleteService(gMaps);
      if (active) {
        setPlacesReady(ready);
      }
    };

    loadPlaces();

    return () => {
      active = false;
    };
  }, [gMaps]);

  useEffect(() => {
    let active = true;

    if (!placesReady || !autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results: any) => {
      if (active) {
        let newOptions: any = [];
        if (value) {
          newOptions = [value];
        }
        if (results) {
          newOptions = [...newOptions, ...results];
        }
        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch, placesReady]);

  const getCity = (geocodingResult: any) => {
    const cityComponent = R.find((addressComponents: any) => {
      return R.includes('locality', addressComponents.types);
    }, geocodingResult.address_components || []);
    return (cityComponent && cityComponent.long_name) || '';
  };
  const getZipCode = (geocodingResult: any) => {
    const zipCodeComponent = R.find((addressComponents: any) => {
      return R.includes('postal_code', addressComponents.types);
    }, geocodingResult.address_components || []);
    return (zipCodeComponent && zipCodeComponent.long_name) || '';
  };
  const getState = (geocodingResult: any) => {
    const stateComponent = R.find((addressComponents: any) => {
      return R.includes('administrative_area_level_1', addressComponents.types);
    }, geocodingResult.address_components || []);
    return (stateComponent && stateComponent.short_name) || '';
  };

  if (!placesReady) {
    return (
      <div className="cmn-map-places-container">
        <TextField
          autoFocus={autoFocus}
          placeholder="Search for a place"
          size="small"
          variant="outlined"
          disabled
          fullWidth
        />
      </div>
    );
  }

  return (
    <>
      <div className="cmn-map-places-container">
        <Autocomplete
          classes={{
            popper: 'cmn-map-places-popper',
            paper: 'cmn-map-places-paper',
            option: 'cmn-map-places-option',
          }}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
          filterOptions={(x) => x}
          options={options}
          autoComplete
          includeInputInList
          filterSelectedOptions
          value={value}
          openOnFocus={true}
          onChange={(event: any, newValue: any) => {
            if (!props.gMaps?.Geocoder || !props.gMap) {
              setValue(newValue);
              return;
            }
            const geocoder = new props.gMaps.Geocoder();
            if (newValue && newValue.place_id) {
              geocoder.geocode({ placeId: newValue.place_id }, (results: any, status: any) => {
                if (status === 'OK') {
                  props.gMap.setCenter(results[0].geometry.location);
                  props.gMap.setZoom(18);
                  props.gMap.panTo(results[0].geometry.location);

                  const mapState: IMap = mapStore.map;
                  mapState.places = newValue;
                  mapState.city = getCity(results[0]);
                  mapState.zip = getZipCode(results[0]);
                  mapState.state = getState(results[0]);
                  mapState.address = newValue.description
                    ? newValue.description.substring(0, newValue.description.lastIndexOf(getCity(results[0])) - 2)
                    : '';
                  dispatch(updateMap(mapState));
                  props.onPlaceSelect?.({
                    address: mapState.address,
                    city: mapState.city,
                    state: mapState.state,
                    zip: mapState.zip,
                    lat: results[0].geometry.location.lat(),
                    lng: results[0].geometry.location.lng(),
                    raw: results[0],
                  });
                } else {
                  alert('Geocode was not successful for the following reason: ' + status);
                }
              });
            }
            setOptions(newValue ? [newValue, ...options] : options);
            setValue(newValue);
          }}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          renderInput={(params) => (
            <>
              <TextField {...params} autoFocus={autoFocus} placeholder="Search for a place" size="small" variant="outlined" />
            </>
          )}
          renderOption={(option: any) => {
            const matches = option.structured_formatting.main_text_matched_substrings;
            const parts = parse(
              option.structured_formatting.main_text,
              matches.map((match: any) => [match.offset, match.offset + match.length])
            );
            return (
              <Grid container alignItems="center">
                <Grid item></Grid>
                <Grid item xs>
                  {parts.map((part: any, index: any) => (
                    <span
                      className={`cmn-map-places-main-text ${part.highlight ? 'is-highlight' : ''}`}
                      key={index}
                    >
                      {part.text}
                    </span>
                  ))}
                  <Typography className="cmn-map-places-secondary-text" variant="body2">
                    {option.structured_formatting.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            );
          }}
        />
      </div>
    </>
  );
};

export default PlacesSearch;
